import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { MESES, MES_LABEL, clientDisplayName } from './tributarioUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const NuevaDeclaracionDialog = ({ open, onOpenChange, formulario, onCreated }) => {
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [modo, setModo] = useState('existente');
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const year = new Date().getFullYear();
  const [form, setForm] = useState({ client_id: '', client_nombre: '', nit: '', dv: '', tipo_persona: '', anio: formulario?.periodicidad === 'mensual' ? year : year - 1, periodo: '', responsable_id: '', notas: '' });

  useEffect(() => {
    if (!open) return;
    Promise.all([axios.get(`${BACKEND_URL}/api/clients`), axios.get(`${BACKEND_URL}/api/users`).catch(() => ({ data: [] }))])
      .then(([c, u]) => { setClients(c.data || []); setUsers(u.data || []); });
    setForm((f) => ({ ...f, client_id: '', client_nombre: '', nit: '', dv: '', tipo_persona: '', periodo: '', anio: formulario?.periodicidad === 'mensual' ? year : year - 1 }));
    setSearch('');
  }, [open, formulario, year]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return clients.filter((c) => !q || clientDisplayName(c).toLowerCase().includes(q) || (c.nit || c.numero_documento || '').includes(q)).slice(0, 8);
  }, [clients, search]);

  const pickClient = (c) => {
    setForm((f) => ({ ...f, client_id: c._id, client_nombre: clientDisplayName(c), nit: c.nit || c.numero_documento || '', dv: c.dv || '', tipo_persona: c.tipo_persona || '' }));
    setSearch(clientDisplayName(c));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_nombre || !form.nit) { toast.error('Indica el cliente y su NIT/documento'); return; }
    if (formulario.periodicidad === 'mensual' && !form.periodo) { toast.error('Selecciona el periodo (mes)'); return; }
    setSaving(true);
    try {
      const payload = { ...form, formulario: formulario.key, anio: Number(form.anio), periodo: formulario.periodicidad === 'mensual' ? form.periodo : null, client_id: modo === 'existente' ? form.client_id || null : null, responsable_id: form.responsable_id || null };
      const { data } = await axios.post(`${BACKEND_URL}/api/tributario/declaraciones`, payload);
      toast.success('Declaración creada');
      onCreated(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo crear');
    } finally { setSaving(false); }
  };

  if (!formulario) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" aria-describedby={undefined} data-testid="nueva-declaracion-dialog">
        <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit' }}>Nueva declaración · {formulario.nombre}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="flex gap-1 rounded-lg bg-slate-100 p-1 text-xs font-medium">
            {[['existente', 'Cliente del portal'], ['nuevo', 'Datos de un nuevo cliente']].map(([k, l]) => (
              <button type="button" key={k} onClick={() => setModo(k)} className={`flex-1 rounded-md py-1.5 transition-colors ${modo === k ? 'bg-white shadow text-[#1F3864]' : 'text-slate-500'}`} data-testid={`nueva-decl-modo-${k}`}>{l}</button>
            ))}
          </div>
          {modo === 'existente' ? (
            <div className="relative">
              <Label>Buscar cliente</Label>
              <Input value={search} onChange={(e) => { setSearch(e.target.value); setForm((f) => ({ ...f, client_id: '' })); }} placeholder="Razón social, nombre o NIT…" data-testid="nueva-decl-buscar-cliente" />
              {search && !form.client_id && (
                <div className="absolute z-20 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg max-h-56 overflow-auto">
                  {filtered.length === 0 && <div className="px-3 py-2 text-xs text-slate-500">Sin coincidencias</div>}
                  {filtered.map((c) => (
                    <button type="button" key={c._id} onClick={() => pickClient(c)} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50" data-testid={`nueva-decl-cliente-${c._id}`}>
                      <span className="font-medium text-slate-800">{clientDisplayName(c)}</span>
                      <span className="ml-2 text-xs text-slate-500">{c.nit || c.numero_documento}</span>
                    </button>
                  ))}
                </div>
              )}
              {form.client_id && <p className="mt-1 text-xs text-emerald-700">Seleccionado: {form.client_nombre} · {form.nit}{form.dv ? `-${form.dv}` : ''}</p>}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Razón social / Nombre</Label><Input value={form.client_nombre} onChange={(e) => setForm({ ...form, client_nombre: e.target.value })} data-testid="nueva-decl-nombre" /></div>
              <div><Label>NIT / Documento</Label><Input value={form.nit} onChange={(e) => setForm({ ...form, nit: e.target.value })} data-testid="nueva-decl-nit" /></div>
              <div><Label>DV</Label><Input value={form.dv} onChange={(e) => setForm({ ...form, dv: e.target.value })} maxLength={1} data-testid="nueva-decl-dv" /></div>
              <div className="col-span-2"><Label>Tipo de persona</Label>
                <select className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm" value={form.tipo_persona} onChange={(e) => setForm({ ...form, tipo_persona: e.target.value })} data-testid="nueva-decl-tipo-persona">
                  <option value="">—</option><option value="Jurídica">Jurídica</option><option value="Natural">Natural</option>
                </select>
              </div>
            </div>
          )}
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Año gravable</Label><Input type="number" value={form.anio} onChange={(e) => setForm({ ...form, anio: e.target.value })} data-testid="nueva-decl-anio" /></div>
            {formulario.periodicidad === 'mensual' && (
              <div><Label>Periodo</Label>
                <select className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm" value={form.periodo} onChange={(e) => setForm({ ...form, periodo: e.target.value })} data-testid="nueva-decl-periodo">
                  <option value="">Mes…</option>{MESES.map((m) => <option key={m} value={m}>{m} · {MES_LABEL[m]}</option>)}
                </select>
              </div>
            )}
            <div className={formulario.periodicidad === 'mensual' ? '' : 'col-span-2'}><Label>Responsable</Label>
              <select className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm" value={form.responsable_id} onChange={(e) => setForm({ ...form, responsable_id: e.target.value })} data-testid="nueva-decl-responsable">
                <option value="">Yo</option>{users.map((u) => <option key={u._id} value={u._id}>{u.name}</option>)}
              </select>
            </div>
          </div>
          <div><Label>Notas</Label><Input value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} placeholder="Opcional" data-testid="nueva-decl-notas" /></div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving} className="bg-[#1F3864] hover:bg-[#162a4d]" data-testid="nueva-decl-submit">{saving ? 'Creando…' : 'Crear declaración'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
